import posthog from "posthog-js";

type EventProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type PostAuthContext = {
  userId: string;
  clientId: string;
  userRole: string;
  lang: string;
};

const apiPath = process.env.VUE_APP_API_PATH || "unknown";
const posthogApiKey = process.env.VUE_APP_POSTHOG_KEY || "";

/**
 * Two separate concerns, mirroring the mobile app (react-native-prototype):
 *
 *   - posthogEnabled — whether PostHog initialises at all. Off in local dev
 *     (`yarn serve` runs NODE_ENV=development), off wherever
 *     VUE_APP_POSTHOG_ENABLED=false, and off with no project key. Deployed
 *     envs (production, staging, QA) are on; QA/staging set NODE_ENV=staging,
 *     so we gate on !== "development" rather than === "production".
 *
 *   - POSTHOG_CONSOLE_DEBUG — in-code switch that forces init on under local
 *     dev AND turns on verbose PostHog console logging, so events can be
 *     confirmed from `yarn serve`. ⚠️ Defaults to false; never commit it as
 *     true — it burns events + plan limits into the shared project for every
 *     developer. Mirrors the mobile POSTHOG_CONSOLE_DEBUG.
 */
const POSTHOG_CONSOLE_DEBUG = false;

const allowedInRuntime =
  process.env.NODE_ENV !== "development" || POSTHOG_CONSOLE_DEBUG;

const posthogEnabled =
  allowedInRuntime &&
  process.env.VUE_APP_POSTHOG_ENABLED !== "false" &&
  posthogApiKey.length > 0;

let initialised = false;
let currentUserId: string | null = null;
let currentClientId: string | null = null;
let currentPageKey: string | null = null;

export const userRoleMap: Record<string, string> = {
  u: "User",
  cl: "Client Admin",
  sp: "Service Provider",
};

/**
 * Strip sensitive query parameters AND path-segment tokens from a URL string.
 * Used by sanitize_properties to prevent PII leaking into PostHog via
 * $current_url, $referrer, $pathname, etc.
 *
 * Covers three shapes found across our stack:
 *   - Query tokens in login, SSO, verify, reset, 2FA and email-deeplink flows
 *     (e.g. ?auth_key=, ?key=, ?oauth=, ?data=, ?returnUrl=)
 *   - Path tokens in routes where the credential IS a URL segment
 *     (e.g. /submit-feedback/<TOKEN>/..., /reset/<AUTH_KEY> from core-api)
 *   - The `id` query on /reset, which carries the username alongside auth_key
 *     (stripped only on that path so benign `?id=` elsewhere is preserved)
 */
const SENSITIVE_QUERY_PARAMS = [
  "auth",
  "auth_key",
  "access_token",
  "code",
  "token",
  "session",
  "email",
  "key",
  "oauth",
  "data",
  "returnUrl",
  "reset_token",
  "recoveryToken",
  "hash",
  "state",
  "nonce",
  "id_token",
];

const sensitiveParamsRegex = new RegExp(
  `([?&])(?:${SENSITIVE_QUERY_PARAMS.join("|")})=[^&#]*`,
  "g",
);

const pathTokenRegex = /(\/(?:submit-feedback|reset))\/[^/?#]+/g;

const resetIdRegex = /(\/reset\b[^#]*?)([?&])id=[^&#]*/g;

export const stripSensitiveParams = (url: string): string => {
  if (!url) return url;
  return url
    .replace(pathTokenRegex, "$1/:token")
    .replace(resetIdRegex, "$1$2")
    .replace(sensitiveParamsRegex, (_, separator) => separator)
    .replace(/\?&+/g, "?")
    .replace(/&{2,}/g, "&")
    .replace(/[?&](?=#|$)/g, "");
};

/**
 * Convert camelCase route name to Title Case for PostHog page_key.
 * e.g. "digitalGym" → "Digital Gym", "myProgress" → "My Progress"
 */
export const routeNameToPageKey = (routeName: string): string => {
  return routeName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (character) => character.toUpperCase())
    .trim();
};

export const initialiseAnalytics = (): void => {
  if (initialised) return;
  if (!posthogEnabled) {
    initialised = true;
    return;
  }

  // PostHog
  posthog.init(posthogApiKey, {
    api_host: "https://eu.i.posthog.com",
    // `always` (not `identified_only`) so anonymous pre-login events get person
    // processing and the anon→identified merge on `identify()` stitches the
    // pre-login session (incl. login failures) onto the user — otherwise auth
    // funnel/issue context is lost. Mirrored in NEP and the mobile app.
    person_profiles: "always",

    // Tag every event with the environment's API domain
    loaded: (ph) => {
      ph.register({
        environment: apiPath,
      });
      if (POSTHOG_CONSOLE_DEBUG) ph.debug();
    },

    // Rage clicks are captured automatically when autocapture is not false.
    // Dead clicks require explicit opt-in.
    autocapture: {
      css_selector_allowlist: [], // block all general autocapture
      capture_copied_text: false,
    },
    capture_dead_clicks: true,

    // Strip auth tokens, emails, and session IDs from URLs before they reach PostHog.
    // Covers event-level URL fields AND the $initial_* person properties PostHog derives
    // from the first pageview (these show up as "Initial current URL" in the UI).
    sanitize_properties: (
      properties: Record<string, unknown>,
    ): Record<string, unknown> => {
      const urlKeys = [
        "$current_url",
        "$referrer",
        "$pathname",
        "$initial_current_url",
        "$initial_referrer",
        "$initial_pathname",
      ];
      for (const key of urlKeys) {
        if (typeof properties[key] === "string") {
          properties[key] = stripSensitiveParams(properties[key] as string);
        }
      }
      return properties;
    },

    // Privacy & PII masking
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      // TODO: PM spec suggests maskTextSelector: '' with selective ph-mask classes
      // Currently masking all text for privacy safety — revisit when ph-mask audit is complete
      maskTextSelector: "*",
    },
    mask_all_text: true,
    mask_all_element_attributes: true,
    ip: false,
  });

  // Google Analytics
  // TODO: initialise GA when ready
  // e.g. gtag('config', process.env.VUE_APP_GA_TRACKING_ID)

  initialised = true;
};

export const trackEvent = (
  eventName: string,
  properties?: EventProperties,
): void => {
  if (!posthogEnabled) return;

  // PostHog — always include user_id and client_id so historic events remain attributable
  // even if PostHog person properties are overridden by a later identity merge
  posthog.capture(eventName, {
    user_id: currentUserId,
    client_id: currentClientId,
    ...properties,
  });

  // Google Analytics
  // TODO: send event to GA when ready
  // e.g. gtag('event', eventName, properties)
};

export const identifyUser = (context: PostAuthContext): void => {
  currentUserId = context.userId;
  currentClientId = context.clientId;
  if (!posthogEnabled) return;

  posthog.identify(context.userId);

  posthog.register({
    user_id: context.userId,
    client_id: context.clientId,
    user_role: userRoleMap[context.userRole] ?? context.userRole,
    lang: context.lang,
  });
};

export const setPersonAttributes = (attributes: EventProperties): void => {
  if (!posthogEnabled) return;
  posthog.setPersonProperties(attributes);
};

export const registerLang = (lang: string): void => {
  if (!posthogEnabled) return;
  posthog.register({ lang });
};

export const resetUser = (): void => {
  currentUserId = null;
  currentClientId = null;

  if (!posthogEnabled) return;

  posthog.reset();
  // posthog.reset() clears ALL super properties including environment registered during init.
  // Re-register environment and page tracking so subsequent anonymous events are still tagged.
  posthog.register({ environment: apiPath });

  initialisePageTracking();
};

/**
 * Set the initial page_key super property on app load (before the router resolves).
 * Falls back to window.location.pathname since the Vue Router hasn't resolved a route name yet.
 */
export const initialisePageTracking = (): void => {
  if (!posthogEnabled) return;

  currentPageKey = window.location.pathname;

  posthog.register({
    page_key: currentPageKey,
    last_page_key: null,
  });
};

/**
 * Update the page_key / last_page_key super properties on SPA route change.
 * Called from router.afterEach in main.ts. Uses route names (converted from
 * camelCase to Title Case) rather than paths for human-readable PostHog data.
 */
export const updatePageKey = (toRouteName: string | null | undefined): void => {
  if (!posthogEnabled) return;

  const newPageKey = toRouteName
    ? routeNameToPageKey(toRouteName)
    : window.location.pathname;

  posthog.register({
    last_page_key: currentPageKey,
    page_key: newPageKey,
  });

  currentPageKey = newPageKey;
};
